import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from './AnimatedSection';
import BackToTop from './BackToTop';

/** What we build, grouped so the list reads as a system rather than a feature dump. */
const capabilityGroups = [
  {
    group: 'Sell',
    items: ['Point of Sale & Checkout', 'QR Menu & Self-Service', 'Online Ordering & E-Commerce', 'Reservations & Booking'],
  },
  {
    group: 'Operate',
    items: ['Preparation & Operational Workflows', 'Inventory & Stock Management', 'Procurement & Purchasing', 'Staff & Workforce Management'],
  },
  {
    group: 'Fulfil',
    items: ['Delivery & Fulfilment', 'Third-Party Delivery Integration', 'Multi-Branch Management'],
  },
  {
    group: 'Understand',
    items: ['Accounting & Financial Operations', 'Business Analytics & Dashboards', 'Customer Relationship Management', 'Loyalty & Engagement', 'AI Automation & Assistants'],
  },
];

const customSolutions = [
  'Business Management Systems',
  'POS & Commerce Platforms',
  'E-Commerce Solutions',
  'ERP Modules',
  'Customer & Loyalty Systems',
  'Workflow Automation',
  'AI Solutions',
  'Mobile & Web Applications',
  'Business Intelligence Platforms',
  'Industry-Specific Operating Systems',
];

const About: React.FC = () => {
  return (
    <div className="relative">
      {/* ---------------------------------------------------------- */}
      {/* Hero                                                        */}
      {/* ---------------------------------------------------------- */}
      <section id="about" className="px-4 sm:px-6 lg:px-8 pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection animation="slide-up">
            <p className="section-label">About Nexasolutra</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight text-balance leading-[1.08] mb-8 max-w-4xl">
              Building the future of{' '}
              <span className="text-primary-500">hospitality &amp; commerce.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted leading-relaxed max-w-3xl">
              Nexasolutra is a technology company building AI-powered operating systems for hospitality
              and commerce. We design intelligent software that helps businesses manage operations,
              automate workflows, understand their customers, and grow more efficiently.
            </p>
          </AnimatedSection>

          <AnimatedSection animation="fade-in" delay={150}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-surface-border mt-14 md:mt-20 rounded-2xl overflow-hidden border border-surface-border">
              {[
                { k: 'Core verticals', v: 'Hospitality & Commerce' },
                { k: 'Built for', v: 'Multi-branch scale' },
                { k: 'Based in', v: 'Abuja, Nigeria' },
              ].map((stat) => (
                <div key={stat.k} className="bg-surface p-5 md:p-6">
                  <p className="text-[11px] uppercase tracking-widest text-muted mb-2">{stat.k}</p>
                  <p className="font-display text-lg md:text-xl font-semibold text-white leading-tight">
                    {stat.v}
                  </p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Positioning                                                 */}
      {/* ---------------------------------------------------------- */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-surface-border">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <AnimatedSection animation="slide-up" className="lg:col-span-5">
            <p className="section-label">Who we are</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight text-balance">
              Technology that works together.
            </h2>
          </AnimatedSection>

          <AnimatedSection animation="slide-up" delay={100} className="lg:col-span-7">
            <div className="space-y-6 text-lg text-muted leading-relaxed">
              <p>
                We believe retailers, commerce-driven brands, wine &amp; spirits businesses, hotels,
                lounges, cafés, and restaurants should not have to run their businesses through
                disconnected software, spreadsheets, and manual processes.
              </p>
              <p className="text-white">Nexasolutra builds the technology that connects them.</p>
              <p>
                Our mission is to become Africa&apos;s leading technology company for AI-powered
                hospitality and commerce infrastructure.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* What we do                                                  */}
      {/* ---------------------------------------------------------- */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-surface-border">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection animation="slide-up">
            <div className="max-w-2xl mb-12 md:mb-16">
              <p className="section-label">What we do</p>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight mb-5 text-balance">
                We connect the critical functions of a modern business.
              </h2>
              <p className="section-body">
                Our systems move businesses from fragmented operations to connected, data-driven ones.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {capabilityGroups.map((group, i) => (
              <AnimatedSection key={group.group} animation="slide-up" delay={i * 80}>
                <div className="brand-card p-6 h-full">
                  <div className="flex items-baseline gap-3 mb-5 pb-4 border-b border-surface-border">
                    <span className="font-display text-xs text-primary-500 uppercase tracking-[0.2em]">
                      {group.group}
                    </span>
                    <span className="text-white/15 font-display text-sm ml-auto">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <ul className="space-y-3">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-muted leading-snug">
                        <span className="w-1 h-1 rounded-full bg-primary-500 mt-1.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Our difference                                              */}
      {/* ---------------------------------------------------------- */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-surface-border">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection animation="slide-up">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              <div>
                <p className="section-label">Our difference</p>
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight mb-6 text-balance">
                  Most companies build tools.
                  <br />
                  <span className="text-primary-500">We build operating systems.</span>
                </h2>
                <div className="space-y-5 text-muted leading-relaxed">
                  <p>
                    We look beyond individual features and focus on how a business actually operates —
                    connecting sales, inventory, finance, customers, employees, and intelligence into
                    systems that reduce manual work and improve visibility.
                  </p>
                  <p className="text-white font-medium">
                    We don&apos;t simply digitize existing processes. We redesign how businesses operate.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-surface-border rounded-2xl overflow-hidden border border-surface-border">
                {[
                  { t: 'Operational efficiency', d: 'Fewer manual steps between doing the work and recording it.' },
                  { t: 'Automation', d: 'Systems that act on their own data instead of waiting on people.' },
                  { t: 'Scalability', d: 'One operating standard that holds from one branch to many.' },
                  { t: 'Measurable growth', d: 'Success measured in margin and clarity, not feature counts.' },
                ].map((item) => (
                  <div key={item.t} className="bg-surface p-6">
                    <h3 className="font-display text-base font-semibold text-white mb-2">{item.t}</h3>
                    <p className="text-sm text-muted leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Infrastructure                                              */}
      {/* ---------------------------------------------------------- */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-surface-border">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection animation="slide-up">
            <div className="max-w-2xl mb-12">
              <p className="section-label">Hospitality &amp; commerce infrastructure</p>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight text-balance">
                Operations and commerce are the same problem.
              </h2>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="fade-in" delay={150}>
            <p className="font-display text-xl md:text-2xl text-white font-semibold leading-snug max-w-3xl">
              Modern businesses need technology that connects operations with commerce. That is where
              Nexasolutra operates — building the infrastructure that lets businesses sell, operate,
              automate, and grow.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Custom solutions                                            */}
      {/* ---------------------------------------------------------- */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-surface-border">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <AnimatedSection animation="slide-up" className="lg:col-span-5">
            <p className="section-label">Custom solutions</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight mb-5 text-balance">
              Built around your business model.
            </h2>
            <p className="text-muted leading-relaxed">
              Beyond our own products, we design and build custom technology for unique operational and
              commercial requirements — each solution built with scalability and long-term digital
              transformation in mind.
            </p>
          </AnimatedSection>

          <AnimatedSection animation="slide-up" delay={100} className="lg:col-span-7">
            <div className="flex flex-wrap gap-2.5">
              {customSolutions.map((item) => (
                <span
                  key={item}
                  className="px-4 py-2.5 rounded-lg border border-surface-border bg-surface-card/60 text-sm text-muted hover:text-white hover:border-primary-500/40 hover:bg-primary-500/5 transition-all duration-300"
                >
                  {item}
                </span>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Vision / mission / philosophy                               */}
      {/* ---------------------------------------------------------- */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 border-t border-surface-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {[
              {
                label: 'Our vision',
                body: "To become Africa's leading technology company building the intelligent infrastructure behind the future of hospitality and commerce.",
              },
              {
                label: 'Our mission',
                body: 'To empower businesses with AI-powered technology that connects operations, commerce, customers, and intelligence — reducing complexity, improving efficiency, increasing profitability, and enabling sustainable growth.',
              },
            ].map((item, i) => (
              <AnimatedSection key={item.label} animation="slide-up" delay={i * 80}>
                <div className="brand-card p-8 md:p-10 h-full">
                  <p className="text-xs uppercase tracking-[0.2em] text-primary-500 mb-5">
                    {item.label}
                  </p>
                  <p className="font-display text-xl md:text-2xl text-white font-semibold leading-snug">
                    {item.body}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection animation="fade-in" delay={150}>
            <div className="rounded-2xl border border-surface-border bg-surface-card p-10 md:p-16 text-center">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-primary-500 mb-6">
                  Our philosophy
                </p>
                <p className="font-display text-2xl md:text-4xl font-semibold text-white leading-tight mb-3">
                  We don&apos;t just build software.
                </p>
                <p className="font-display text-2xl md:text-4xl font-semibold text-primary-500 leading-tight mb-10">
                  We build the systems businesses run on.
                </p>
                <Link to="/contact" className="btn-primary text-base px-8 py-3.5">
                  Book a Strategy Call
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <BackToTop />
    </div>
  );
};

export default About;
